import { useQueryClient } from '@tanstack/react-query'
import { filter, pipe, reduce, values } from 'lodash/fp'
import { RotateCcwIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	optimizationQueries,
	resetOptimizations,
	useBulkOptimizationMutation,
	useImportOptimizationGroups,
} from '@/entities/optimization'

import { confirm } from '@/shared/components/ui/confirmer'
import { OptimizationAction, OptimizationStatus } from '@/shared/generated'

import { CommandMenuItem } from './CommandMenuItem'

export const ResetOptimizationGroups = ({
	onHighlight,
	onSelect,
}: {
	onHighlight: () => void
	onSelect: (command: () => void) => void
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const importOptimizationGroupsMutation = useImportOptimizationGroups()
	const bulkActionMutation = useBulkOptimizationMutation()
	const onSubmit = async () => {
		try {
			const optimizations = await queryClient.fetchQuery(
				optimizationQueries.list()
			)
			const items = Object.values(optimizations)
			const hasLockedOptimizations = items?.some(
				(optimization) =>
					optimization.is_exported &&
					optimization.status === OptimizationStatus.Locked
			)
			if (hasLockedOptimizations) {
				const answer = await confirm({
					title: t('action_confirm_title'),
					description: t('optimization_remove_export_message'),
					actionText: t('general_remove'),
					cancelText: t('general_cancel'),
				})
				if (answer) {
					await bulkActionMutation.mutateAsync(
						items.map(({ optimization_group_id }) => ({
							optimization_group_id,
							action: OptimizationAction.DeleteExport,
						}))
					)
				} else {
					return
				}
			}
			const selected = items.map(
				({ optimization_group_id }) => optimization_group_id
			)
			await resetOptimizations(optimizations || [], selected || [])
			toast.promise(
				importOptimizationGroupsMutation.mutateAsync({
					product_diffs_file: new File([], 'empty.csv'),
					ignore_empties: true,
					optimization_group_ids: JSON.stringify(selected),
					file_header_mapping: '{}',
				}),
				{
					success: (data) => {
						const { sku_total, og_total } = getBulkImportResult(data)

						return `${t('og_bulk_import_will_be_processed_items')} ${sku_total} ${t('og_bulk_import_will_be_processed_og')} ${og_total}`
					},
					loading: 'Executing...',
					error: 'Executing failed',
				}
			)
		} catch (e) {
			toast.error('Error')
		}
	}

	return (
		<CommandMenuItem
			value='Reset All optimization groups'
			keywords={['optimization groups', 'Reset All optimization groups']}
			onHighlight={onHighlight}
			onSelect={() => onSelect(onSubmit)}
		>
			<RotateCcwIcon className='h-4 w-4' />
			Reset All optimization groups
		</CommandMenuItem>
	)
}

const getBulkImportResult = pipe([
	values,
	filter((v: number) => v > 0),
	reduce(
		(acc, data: number) => ({
			og_total: acc.og_total + 1,
			sku_total: acc.sku_total + data,
		}),
		{
			og_total: 0,
			sku_total: 0,
		}
	),
])
